import React, { useMemo, useState } from 'react';
import { formatCurrency } from '../../utils/currencyUtils';
import PaymentModeBadge from '../shared/PaymentModeBadge';
import { SALARY_THEME } from './salaryTheme';
import {
  ArrowDownAZ,
  ArrowUpAZ,
  Banknote,
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Pencil,
  Search,
  Trash2,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';

function parsePaymentTime(entry) {
  if (entry.date) {
    const d = new Date(entry.date);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  if (entry.month) {
    const d = new Date(`${entry.month}-01`);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  if (entry.createdAt?.toDate) return entry.createdAt.toDate().getTime();
  if (entry.createdAt?.seconds) return entry.createdAt.seconds * 1000;
  return 0;
}

function formatMonthLabel(monthStr) {
  if (!monthStr) return 'Unknown period';
  const d = new Date(`${monthStr}-01`);
  if (Number.isNaN(d.getTime())) return monthStr;
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function formatPayDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function SalaryPaymentHistory({
  salaryPayments,
  totalPaid,
  companyCurrency,
  expandedSalaryCards,
  setExpandedSalaryCards,
  onViewSlip,
  onEdit,
  onDelete,
  onExportCSV,
  onExportExcel,
  onExportPDF,
}) {
  const fmt = (n) => formatCurrency(Number(n) || 0, companyCurrency);
  const [search, setSearch] = useState('');
  const [sortNewestFirst, setSortNewestFirst] = useState(true);

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = [...salaryPayments];
    if (q) {
      list = list.filter(
        (e) =>
          (e.employeeName || '').toLowerCase().includes(q) ||
          (e.employeeId || '').toLowerCase().includes(q) ||
          (e.month || '').includes(q) ||
          (e.paymentMode || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const diff = parsePaymentTime(a) - parsePaymentTime(b);
      return sortNewestFirst ? -diff : diff;
    });
    return list;
  }, [salaryPayments, search, sortNewestFirst]);

  const numberedEntries = useMemo(
    () => filteredSorted.map((entry, i) => ({ entry, rowNum: i + 1 })),
    [filteredSorted]
  );

  const groupedByMonth = useMemo(() => {
    const groups = [];
    let currentKey = null;
    numberedEntries.forEach(({ entry, rowNum }) => {
      const key = entry.month || 'unknown';
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ monthKey: key, label: formatMonthLabel(key), entries: [] });
      }
      groups[groups.length - 1].entries.push({ entry, rowNum });
    });
    return groups;
  }, [numberedEntries]);

  const stats = useMemo(() => {
    const count = filteredSorted.length;
    const earnings = filteredSorted.reduce(
      (s, e) => s + (Number(e.totalEarnings || e.total) || 0),
      0
    );
    const deductions = filteredSorted.reduce((s, e) => s + (Number(e.totalDeductions) || 0), 0);
    const net = filteredSorted.reduce((s, e) => s + (Number(e.netAmount || e.total) || 0), 0);
    return { count, earnings, deductions, net };
  }, [filteredSorted]);

  const t = SALARY_THEME;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-md overflow-hidden ring-1 ring-black/5">
      <div className={`bg-gradient-to-r ${t.header} px-5 py-5 text-white`}>
        <h3 className="text-lg font-semibold tracking-tight">Payment history</h3>
        <p className="text-slate-300 text-sm mt-1">
          All salary payments · sorted {sortNewestFirst ? 'newest first' : 'oldest first'}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gradient-to-b from-slate-50 to-white border-b border-gray-100">
        <div className="rounded-xl border border-blue-100 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <Users className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Payments</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{stats.count}</p>
        </div>
        <div className="rounded-xl border border-sky-100 bg-white p-3 shadow-sm">
          <div className={`flex items-center gap-2 ${t.earningsStat} mb-1`}>
            <TrendingUp className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Earnings</span>
          </div>
          <p className={`text-lg font-bold truncate ${t.earningsStatValue}`}>{fmt(stats.earnings)}</p>
        </div>
        <div className="rounded-xl border border-red-100 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2 text-red-500 mb-1">
            <TrendingDown className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Deductions</span>
          </div>
          <p className="text-lg font-bold text-red-600 truncate">{fmt(stats.deductions)}</p>
        </div>
        <div className="rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-3 shadow-sm">
          <div className="flex items-center gap-2 text-sky-600 mb-1">
            <Banknote className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Net paid</span>
          </div>
          <p className="text-lg font-bold text-sky-800 truncate">{fmt(stats.net || totalPaid)}</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 px-4 py-3 border-b border-gray-100 bg-white">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee, ID, month, mode…"
            className={`w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:bg-white ${t.filterFocus}`}
          />
        </div>
        <button
          type="button"
          onClick={() => setSortNewestFirst((v) => !v)}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 shrink-0"
        >
          {sortNewestFirst ? (
            <>
              <ArrowDownAZ className="h-4 w-4 text-blue-600" />
              Newest first
            </>
          ) : (
            <>
              <ArrowUpAZ className="h-4 w-4 text-blue-600" />
              Oldest first
            </>
          )}
        </button>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button type="button" onClick={onExportCSV} className={t.exportBtn}>
            <Download className="h-4 w-4 opacity-70" aria-hidden />
            CSV
          </button>
          <button type="button" onClick={onExportExcel} className={t.exportBtn}>
            <Download className="h-4 w-4 opacity-70" aria-hidden />
            Excel
          </button>
          <button type="button" onClick={onExportPDF} className={t.exportBtn}>
            <Download className="h-4 w-4 opacity-70" aria-hidden />
            PDF
          </button>
        </div>
      </div>

      {/* Desktop: grouped table */}
      <div className="hidden lg:block">
        {filteredSorted.length === 0 ? (
          <div className="py-16 text-center">
            <div className={`mx-auto w-14 h-14 rounded-full ${t.emptyBg} flex items-center justify-center mb-3`}>
              <Banknote className={`h-7 w-7 ${t.emptyIcon}`} />
            </div>
            <p className="text-gray-500 font-medium">No payments found</p>
            <p className="text-sm text-gray-400 mt-1">
              {search ? 'Try a different search' : 'Run payroll to record the first payment'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white text-left text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-3 w-12 text-center">#</th>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Pay date</th>
                  <th className="px-4 py-3">Salary month</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3 text-right">Earnings</th>
                  <th className="px-4 py-3 text-right">Deductions</th>
                  <th className="px-4 py-3 text-right">Net pay</th>
                  <th className="px-4 py-3 text-center w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedByMonth.map((group) => (
                  <React.Fragment key={group.monthKey}>
                    <tr className={t.groupRow}>
                      <td colSpan={9} className="px-4 py-2.5">
                        <div className={`flex items-center gap-2 ${t.groupLabel} font-semibold text-xs uppercase tracking-wide`}>
                          <Calendar className="h-4 w-4" />
                          {group.label}
                          <span className={`font-normal ${t.groupSub} normal-case`}>
                            ({group.entries.length} payment{group.entries.length !== 1 ? 's' : ''})
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.entries.map(({ entry, rowNum }) => {
                      const zebra = rowNum % 2 === 0;
                      return (
                        <tr
                          key={entry.id}
                          className={`border-b border-gray-100 transition-colors ${t.hoverRow} ${
                            zebra ? 'bg-white' : 'bg-slate-50/60'
                          }`}
                        >
                          <td className="px-4 py-3 text-center text-xs font-mono text-gray-400">{rowNum}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`h-9 w-9 rounded-full bg-gradient-to-br ${t.avatar} text-white flex items-center justify-center text-sm font-bold shadow-sm shrink-0`}>
                                {(entry.employeeName || '?').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{entry.employeeName}</p>
                                <p className="text-xs text-gray-500">{entry.employeeId}</p>
                                {entry.post && (
                                  <p className="text-[10px] text-gray-400 mt-0.5">{entry.post}</p>
                                )}
                                {entry.settledFromOtherWallet?.amount > 0 && (
                                  <p className="text-[10px] text-amber-700 flex items-center gap-0.5 mt-1">
                                    <Wallet className="h-3 w-3" />
                                    {entry.settledFromOtherWallet.sourceEmployeeName}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-800 whitespace-nowrap font-medium">
                            {formatPayDate(entry.date)}
                          </td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{entry.month || '—'}</td>
                          <td className="px-4 py-3">
                            <PaymentModeBadge mode={entry.paymentMode} />
                          </td>
                          <td className={`px-4 py-3 text-right font-medium tabular-nums ${t.earningsText}`}>
                            {fmt(entry.totalEarnings || entry.total)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-red-600 tabular-nums">
                            {fmt(entry.totalDeductions)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-block rounded-lg px-2.5 py-1 font-bold tabular-nums ${t.netBadge}`}>
                              {fmt(entry.netAmount || entry.total)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => onViewSlip(entry)}
                                className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"
                                title="Salary slip"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => onEdit(entry)}
                                className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => onDelete(entry.id)}
                                className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold text-sm">
                  <td colSpan={5} className="px-4 py-3 text-right text-gray-700 uppercase text-xs tracking-wide">
                    Totals ({stats.count} rows)
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums ${t.earningsStatValue}`}>{fmt(stats.earnings)}</td>
                  <td className="px-4 py-3 text-right text-red-700 tabular-nums">{fmt(stats.deductions)}</td>
                  <td className="px-4 py-3 text-right text-sky-900 tabular-nums">{fmt(stats.net)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Mobile list */}
      <div className="lg:hidden p-4 space-y-4 max-h-[70vh] overflow-y-auto">
        {filteredSorted.length === 0 ? (
          <p className="text-center py-10 text-gray-400 text-sm">No payments found</p>
        ) : (
          groupedByMonth.map((group) => (
            <div key={group.monthKey}>
              <div className={`sticky top-0 z-10 flex items-center gap-2 py-2 px-2 mb-2 rounded-lg ${t.groupRow} ${t.groupLabel} text-xs font-bold uppercase tracking-wide`}>
                <Calendar className="h-3.5 w-3.5" />
                {group.label}
              </div>
              <ol className="space-y-2 list-none">
                {group.entries.map(({ entry, rowNum }) => {
                  const open = expandedSalaryCards.has(entry.id);
                  return (
                    <li
                      key={entry.id}
                      className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                    >
                      <button
                        type="button"
                        className="w-full flex items-center gap-3 p-3.5 text-left"
                        onClick={() => {
                          const next = new Set(expandedSalaryCards);
                          if (open) next.delete(entry.id);
                          else next.add(entry.id);
                          setExpandedSalaryCards(next);
                        }}
                      >
                        <span className="text-xs font-mono text-gray-400 w-6">{rowNum}</span>
                        <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${t.avatar} text-white flex items-center justify-center font-bold text-sm shrink-0`}>
                          {(entry.employeeName || '?').charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{entry.employeeName}</p>
                          <p className="text-xs text-gray-500">{formatPayDate(entry.date)}</p>
                          <PaymentModeBadge mode={entry.paymentMode} />
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-sky-800">{fmt(entry.netAmount || entry.total)}</p>
                          {open ? (
                            <ChevronUp className="w-4 h-4 text-gray-400 ml-auto" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
                          )}
                        </div>
                      </button>
                      {open && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-slate-50/80 space-y-3">
                          <div className="grid grid-cols-3 gap-2 text-center text-xs">
                            <div className="rounded-lg bg-sky-50 py-2 ring-1 ring-sky-100">
                              <p className="text-gray-500">Earn</p>
                              <p className={`font-semibold ${t.earningsText}`}>
                                {fmt(entry.totalEarnings || entry.total)}
                              </p>
                            </div>
                            <div className="rounded-lg bg-red-50 py-2">
                              <p className="text-gray-500">Deduct</p>
                              <p className="font-semibold text-red-600">{fmt(entry.totalDeductions)}</p>
                            </div>
                            <div className="rounded-lg bg-sky-50 py-2">
                              <p className="text-gray-500">Net</p>
                              <p className="font-semibold text-sky-800">
                                {fmt(entry.netAmount || entry.total)}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => onViewSlip(entry)}
                              className="flex-1 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-sky-500 rounded-lg shadow-sm"
                            >
                              Slip
                            </button>
                            <button
                              type="button"
                              onClick={() => onEdit(entry)}
                              className="flex-1 py-2 text-xs font-semibold text-blue-700 bg-blue-50 rounded-lg"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(entry.id)}
                              className="flex-1 py-2 text-xs font-semibold text-red-600 bg-red-50 rounded-lg"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
